// Cursos de un alumno, derivados de sus matrículas, con nombre corto.
//
// Por qué (Esteban, 2026-09-11): el campo "Intereses" del módulo de Estudiantes
// lo escribía a mano una empleada eligiendo etiquetas, y quedaba mal: de 24,583
// alumnos con matrícula, 2,641 tenían sólo parte de sus cursos y 1,072 ninguno,
// y 1,642 arrastraban etiquetas de cursos que nunca llevaron. Ahora la lista se
// calcula sola a partir de las matrículas, con el nombre corto del curso
// ("Refrigeración", no "Diplomado de Refrigeración (SPS)"), el curso activo (o
// el más reciente) primero y sin repetidos.
//
// Nada de esto se guarda: se deriva al mostrar, así que no puede quedar viejo.

const CAMPUS = "SPS|TGU|MTY|Monterrey|Online|Torre[oó]n|SAL"

// Nombres de ct_enrollment_statuses, por si no llega el catálogo del servidor.
const ESTATUS: {[id: number]: string} =
{
	0: "Inactivo", 1: "Activo", 2: "Graduado", 3: "Error", 4: "No comenzado",
	5: "Cambio de horario", 6: "Retirado", 7: "Aplazado", 8: "Especialidad",
}

export interface MatriculaResumen
{
	curso    : string
	horario  : string
	periodo  : string
	maestro  : string
	inscrito : string
	estatus  : string
	activa   : boolean
}

export interface CursoDelAlumno
{
	nombre     : string
	activo     : boolean
	/*
		Terminado: no tiene matricula activa hoy, pero SI se graduo alguna vez.
		Graduarse no es retirarse -- es la otra punta del curso -- y con el
		historico de Classe365 cargado (2026-09-21) miles de alumnos quedaron
		graduados y se veian igual que los que se fueron. (Esteban, 2026-09-21)
	*/
	graduado   : boolean
	matriculas : MatriculaResumen[]
}

/** La clase de la pastilla de un curso. Un solo lugar para que los cuatro
    lados donde se pinta digan lo mismo. */
export const pildoraDeCurso = (curso : CursoDelAlumno) : string =>
	curso.activo ? "bg-success" : (curso.graduado ? "bg-graduado" : "bg-danger")

/** Como se llama ese estado en palabras. */
export const estadoDeCurso = (curso : CursoDelAlumno) : string =>
	curso.activo ? "Activo" : (curso.graduado ? "Graduado" : "Inactivo")

// Conceptos de cobro que viven como "curso" en el ERP pero no son cursos.
const NO_ES_CURSO = /^(gastos de graduaci|fondo de graduaci|paquete de graduaci|graduaci[oó]n |plataforma |personalizado|curso ignus)/i

// El mismo curso escrito distinto según el plantel (acentos, orden de las
// palabras). Clave: el nombre corto sin acentos y en minúsculas.
// Cómo se muestra cada curso. La clave es el nombre ya recortado (sin plantel,
// sin año, sin "Diplomado de"), en minúsculas y sin acentos.
//
// Lista revisada por Esteban el 2026-09-11. Para cambiar cómo se ve un curso se
// edita SÓLO este bloque; el resto del archivo no se toca.
const CANONICO: {[clave: string]: string} =
{
	// variantes del mismo curso según el plantel (acentos, orden de palabras)
	"ingles"                       : "Inglés",
	"mixologia"                    : "Mixología",
	"mixologia y cocteleria"       : "Mixología",
	"hoteleria"                    : "Hotelería",
	"reposteria y pasteleria"      : "Repostería",
	"pasteleria y reposteria"      : "Repostería",
	// nombres cortos pedidos por Esteban
	"acrilismo"                    : "Manicure",
	"batalla de barberos"          : "Barbería",
	"seminario de barberos"        : "Barbería",
	"electronica inverter"         : "Inverter",
	"estetica facial y spa"        : "Estética",
	// "Diplomado de Estética Facial, Masajes y Spa" (nombre oficial desde 2026-09-27): se corta en la coma.
	"estetica facial"              : "Estética",
	"finanzas personales"          : "Finanzas",
	"gastronomia con ingles tecnico" : "Gastronomía",
	"instituto fundacion wa"       : "Fundación",
	"wea international institute"  : "Fundación",
	"inteligencia artificial"      : "Inteligencia",
	"marketing digital"            : "Marketing",
	"marketing para emprendedores" : "Marketing",
	"reparacion de celulares"      : "Celulares",
	"pestanas y microblading"      : "Cejas",
	// "Plataforma X" NO se recorta: es otra cosa que la clase presencial (Esteban,
	// 2026-09-16). Solo se corrige el acento que falta en la base.
	"plataforma ingles"            : "Plataforma Inglés",
	"extension de pestanas"        : "Cejas",
}

// Cursos que se muestran con DOS etiquetas: "Taller" más el tema, para que
// cualquiera que haya tomado un taller quede marcado como tal (Esteban,
// 2026-09-11). El tema sale de la misma lista de nombres cortos de arriba.
const VARIOS: {[clave: string]: string[]} =
{
	"taller"                           : ["Taller"],
	"talleres"                         : ["Taller"],
	"talleres tgu 1"                   : ["Taller"],
	"talleres de diferente areas"      : ["Taller"],
	"taller de cejas y liftip de pestanas" : ["Taller", "Cejas"],
	"taller de galletas new york"      : ["Taller", "Repostería"],
	"taller navideno de comida salada" : ["Taller", "Gastronomía"],
	"taller navideno de postres dulces": ["Taller", "Repostería"],
	"taller automaquillaje"            : ["Taller", "Maquillaje"],
	"taller de rosca de reyes"         : ["Taller", "Panadería"],
}

export const sinAcentos = (texto: any): string =>
	String(texto || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim()

/** "Diplomado de Refrigeración (SPS)" -> "Refrigeración" */
export function cursoCorto(nombre: any): string
{
	let n = String(nombre || "").replace(/\s+/g, " ").trim()
	if(!n) return ""

	n = n.replace(new RegExp(`\\s*\\((${CAMPUS})\\)\\s*$`, "i"), "")   // "(SPS)" al final
	n = n.replace(new RegExp(`\\s+(${CAMPUS})\\s*$`, "i"), "")          // "TGU" suelto al final
	n = n.replace(/\s*\b(19|20)\d{2}\b\s*$/, "")                        // año al final
	n = n.replace(/^(Diplomado|Curso|Programa)\s+(Profesional\s+)?(de|en|del)\s+/i, "")
	n = n.replace(/^(Diplomado|Curso|Programa)\s+/i, "")
	n = n.split(",")[0]                                                  // "Cejas, Pestañas y..." -> "Cejas"
	n = n.replace(/\s+/g, " ").trim()

	// Los que vienen TODO EN MAYÚSCULAS se pasan a capitalización normal.
	if(n && n === n.toUpperCase())
	{
		n = n.toLowerCase()
			.replace(/(^|\s)(\p{L})/gu, (m: string) => m.toUpperCase())
			.replace(/\b(De|Del|Y|La|El|En|Con|Para)\b/g, (m: string) => m.toLowerCase())
	}

	return CANONICO[sinAcentos(n)] || n
}

/** Cómo se muestra un curso: normalmente uno, los talleres dos (Taller + tema). */
export function nombresCortos(nombre: any): string[]
{
	const base = String(nombre || "").replace(/\s+/g, " ").trim()
	if(!base) return []

	// La clave se busca sobre el nombre ya recortado, antes de renombrarlo.
	let recortado = base
		.replace(new RegExp(`\\s*\\((${CAMPUS})\\)\\s*$`, "i"), "")
		.replace(new RegExp(`\\s+(${CAMPUS})\\s*$`, "i"), "")
		.replace(/\s*\b(19|20)\d{2}\b\s*$/, "")
		.replace(/\s+/g, " ").trim()

	const varios = VARIOS[sinAcentos(recortado)]
	if(varios) return varios

	const uno = cursoCorto(base)
	return uno ? [uno] : []
}

export function esCursoReal(nombre: any): boolean
{
	const n = String(nombre || "").trim()
	return n !== "" && !NO_ES_CURSO.test(n)
}

/**
 * Lista de cursos del alumno a partir de sus matrículas: el activo (o el más
 * reciente) primero y sin repetidos. Las matrículas marcadas como ERROR no
 * cuentan: son capturas equivocadas.
 */
/**
 * Cursos del alumno a partir de sus matrículas: el activo (o el más reciente)
 * primero y sin repetidos, cada uno con el detalle de sus matrículas.
 *
 * Un curso se marca ACTIVO si el alumno tiene hoy al menos una matrícula activa
 * en él, aunque en el pasado se haya retirado y vuelto a entrar (Esteban,
 * 2026-09-11). Las matrículas marcadas ERROR no cuentan: son capturas
 * equivocadas.
 */
export function cursosDeAlumno(enrollments: any, estatusRef?: any): CursoDelAlumno[]
{
	const ACTIVA = 1, GRADUADA = 2, ERROR = 3

	const fecha = (e: any): string => String(e?.schedule?.start_date || e?.created_at || "")

	const filas = (Array.isArray(enrollments) ? enrollments : [])
		.filter((e: any) => Number(e?.status_id) !== ERROR)
		.map((e: any) =>
		{
			const crudo    = e?.schedule?.level?.course?.name || ""
			const mentor   = e?.schedule?.mentor
			const activa   = Number(e?.status_id) === ACTIVA
			const graduada = Number(e?.status_id) === GRADUADA
			const estatus  = estatusRef?.["ES_" + Number(e?.status_id)]?.name
				|| ESTATUS[Number(e?.status_id)] || "Sin estatus"

			return {
				nombres : nombresCortos(crudo),
				crudo   : crudo,
				activa  : activa,
				graduada: graduada,
				fecha   : fecha(e),
				resumen :
				{
					curso    : String(crudo).replace(/\s+/g, " ").trim(),
					horario  : String(e?.schedule?.name || "").trim(),
					periodo  : String(e?.schedule?.period?.name || "").trim(),
					maestro  : [mentor?.first_name, mentor?.last_name].filter(Boolean).join(" ").trim(),
					inscrito : fechaCorta(e?.created_at),
					estatus  : estatus,
					activa   : activa,
				} as MatriculaResumen,
			}
		})
		.filter((f: any) => f.nombres.length > 0 && esCursoReal(f.crudo))

	// Más reciente primero; dentro de cada curso el orden queda igual.
	filas.sort((a: any, b: any) =>
		(a.fecha < b.fecha ? 1 : (a.fecha > b.fecha ? -1 : 0)))

	const porNombre = new Map<string, CursoDelAlumno>()
	const orden: string[] = []

	for(const f of filas)
	{
		for(const nombre of f.nombres)
		{
			const clave = sinAcentos(nombre)
			let curso = porNombre.get(clave)

			if(!curso)
			{
				curso = {nombre : nombre, activo : false, graduado : false, matriculas : []}
				porNombre.set(clave, curso)
				orden.push(clave)
			}

			// Verde si tiene AL MENOS UNA matrícula activa, aunque antes se haya retirado.
			if(f.activa)   curso.activo   = true
			if(f.graduada) curso.graduado = true
			curso.matriculas.push(f.resumen)
		}
	}

	// Primero los cursos en los que está activo hoy.
	return orden
		.map((clave: string) => porNombre.get(clave) as CursoDelAlumno)
		.sort((a: CursoDelAlumno, b: CursoDelAlumno) => (a.activo === b.activo ? 0 : (a.activo ? -1 : 1)))
}

/** "2026-09-11T16:27:05.000000Z" -> "11/09/2026" */
function fechaCorta(valor: any): string
{
	const s = String(valor || "")
	const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
	return m ? `${m[3]}/${m[2]}/${m[1]}` : ""
}
